import { AngularFirestoreCollection, CollectionReference, Query, QueryDocumentSnapshot } from "@angular/fire/firestore"

import { ApiModel } from "../models/models"
import { DaoBase, DaoGetDataOptions } from "./dao-base"

export abstract class FirebaseDaoBase<T extends ApiModel, GetDataItemsType = T> extends DaoBase<T, GetDataItemsType> {
  public abstract getCollection(): AngularFirestoreCollection

  public getCollectionQuery(): CollectionReference | Query {
    return this.getCollection().ref
  }

  /**
   * Initial query sets options and gets the data for the DAO.
   *
   * @param options
   * Options to sort or paginate the data
   *
   * @param retrieveAllData
   * When set to true, this will retrieve all items give the options given,
   * and will cache the entire dataset. This allows the grids to still be
   * pageable, sortable and searchable. When set to false, it will limit the query based on the limit value.
   * DO NOT SET TO FALSE. A bug is occurring when on a page > 1 and you click the previous button.
   * Future story will fix this.
   *
   * @returns {Promise<void>} Promise resolves when the data is finished and metadata for DAO is updated.
   */
  public async initialize(noLimit = true): Promise<void> {
    this.options = {
      sortDirection: "asc",
      sortField: null,
      ...(this.options || {}),
    }

    if (noLimit) {
      delete this.options.limit
    }

    this.allDataRetrieved = noLimit
    await this.updateData(this.generateDataQuery())
  }

  /**
   * Retrieves additional data from firestore
   */
  public async refreshData(query?: Query): Promise<void> {
    if (this.allDataRetrieved) {
      this.updateCachedData(this.totalData.value)
    } else {
      await this.updateData(query || this.generateDataQuery())
    }
  }

  public async deleteItem(id: string, deleteItemOnServer?: () => Promise<void>): Promise<void> {
    if (deleteItemOnServer) {
      await deleteItemOnServer()
    }

    if (this.allDataRetrieved) {
      const updatedData = this.totalData.value.filter(item => item._id !== id)
      this.totalData.next(updatedData)
      this.updateCachedData(updatedData)
    } else {
      await this.refreshData(this.generateDataQuery())
    }
  }

  /**
   * Reset the data
   */
  public reset(): void {
    this.currentData.next([])
    this.currentSnapshots.next([])
    this.errorMessage = ""
    this.hasError = false
  }

  /**
   * Returns the data from the given snapshot and adds the _id property as the snapshot.id
   */
  public getDataFromSnapshot(snapshot: QueryDocumentSnapshot<T>): T {
    if (!snapshot.exists) {
      return null
    }

    const item = snapshot.data() as T
    item._id = snapshot.id
    return item
  }

  /**
   * Returns a list of data items from the given snapshots, and adds each item to the dataMap property in the DAO
   */
  public getDataFromSnapshots(docSnapshots: Array<QueryDocumentSnapshot<any>>): T[] {
    return docSnapshots
      .map<T>(snapshot => this.getDataFromSnapshot(snapshot))
      .filter(item => !!item)
  }

  /**
   * Returns the total count based on the page number and page size.
   */
  public getTotalCount(): number {
    return ((this.page.pageIndex || 0) + 1) * this.page.pageSize
  }

  /**
   * Generates the query to firebase in order to get the latest data
   * based on the options and values within this DAO
   */
  private generateDataQuery(): Query {
    const collectionRef = this.getCollectionQuery()

    let query
    if (this.options.sortField) {
      query = this.addSortingToQuery(collectionRef)
    }

    if (this.options.limit) {
      query = this.addLimitToQuery(query || collectionRef)
    }

    const cursor = this.getCursor()
    if (cursor && this.page.pageIndex > 0) {
      query = this.addCursorToQuery(query, cursor)
    }

    return query || collectionRef
  }

  private addSortingToQuery(query: Query): Query {
    return query.orderBy(this.options.sortField, (this.options.sortDirection === "asc" || !this.options.sortDirection) ? "asc" : "desc")
  }

  private addLimitToQuery(query: CollectionReference | Query): Query {
    return query.limit(this.options.limit)
  }

  private addCursorToQuery(query: Query, cursor: QueryDocumentSnapshot<T>): Query {
    return this.isPreviousPage()
          ? query.endBefore(cursor)
          : query.startAfter(cursor)
  }

  /**
   * Firebase will charged you more if you use .offset to get paginated data.
   * For this reason, we are using a cursor, which tells firebase which document
   * to start or end with for the query you will get. This determines that document.
   */
  private getCursor(): QueryDocumentSnapshot<any> {
    const current = this.currentSnapshots.value
    if (current.length) {
      return this.isPreviousPage() ? current[0] : current[current.length - 1]
    }
    return null
  }

  private isPreviousPage(): boolean {
    return this.page.previousPageIndex > this.page.pageIndex
  }

  /**
   * Retrieves the data based on the passed in query and updates
   * appropriate values in this DAO to properly display the data.
   */
  private async updateData(query?: Query): Promise<void> {
    if (this.loading.value) { return }

    this.loading.next(true)

    const querySnapshot = await (query || this.generateDataQuery()).get()
    let values = await this.getDataItems(this.getDataFromSnapshots(querySnapshot.docs))
    values = values.filter(item => !!item)
    values.forEach(item => this.dataMap[item._id] = item)
    this.updateMetadata(values, querySnapshot.docs)

    this.loading.next(false)
  }

  private updateMetadata(values: T[], snapshots: Array<QueryDocumentSnapshot<any>>): void {
    if (!this.allDataRetrieved) {
      this.totalCount = this.getTotalCount()
      if (values.length === 0) {
        if (this.page.pageIndex !== this.page.previousPageIndex) {
          this.revertPageChange()
          this.page.isLastPage = true
        } else {
          this.done.next(true)
        }
      } else if (values.length < this.page.pageSize) {
        this.totalCount = this.getTotalCount()
        this.page.isLastPage = true
        this.currentData.next(values)
        this.currentSnapshots.next(snapshots)
      } else {
        this.page.isLastPage = false
        this.totalCount = this.getTotalCount() + this.page.pageSize
        this.currentData.next(values)
        this.currentSnapshots.next(snapshots)
      }

      this.page.length = this.totalCount

    } else {
      this.totalData.next(values)
      this.updateCachedData(values)
      this.allDataRetrieved = true
    }
  }

  private revertPageChange(): void {
    const isNextPage = !this.isPreviousPage()
    this.page.pageIndex = isNextPage
      ? this.page.pageIndex - 1
      : this.page.pageIndex + 1

    this.page.previousPageIndex = isNextPage
      ? this.page.previousPageIndex - 1
      : this.page.previousPageIndex + 1
    this.totalCount = this.getTotalCount()
  }
}
